'use client';

import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@repo/ui/breadcrumb';
import { Fragment, useMemo } from 'react';
import { Link, useMatches, useParams } from 'react-router';
import { useOrganizationStore } from '@/stores/organization-store';

export type AppRouteHandle = {
  breadcrumb?: string;
  /** Resolved in the breadcrumb UI using the organization store */
  organizationCrumb?: true;
};

function organizationLabel(
  organizationId: string | undefined,
  organizations: { id: string; name: string }[],
  currentOrganization: { id: string; name: string },
) {
  if (!organizationId) {
    return t('workspace.organization');
  }
  const fromList = organizations.find((o) => o.id === organizationId)?.name;
  if (fromList) {
    return fromList;
  }
  if (currentOrganization.id === organizationId) {
    return currentOrganization.name || t('workspace.organization');
  }
  return t('workspace.organization');
}

export function AppBreadcrumb() {
  useLocale();

  const matches = useMatches();
  const params = useParams();
  const organizations = useOrganizationStore((s) => s.organizations);
  const currentOrganization = useOrganizationStore((s) => s.currentOrganization);

  const orgName = useMemo(
    () => organizationLabel(params.organizationId, organizations, currentOrganization),
    [getLocale(), params.organizationId, organizations, currentOrganization],
  );

  const items = useMemo(() => {
    const crumbs: { to: string; label: string }[] = [];

    for (const match of matches) {
      const handle = match.handle as AppRouteHandle | undefined;
      if (handle?.organizationCrumb) {
        crumbs.push({ to: match.pathname, label: orgName });
        continue;
      }
      if (handle?.breadcrumb) {
        crumbs.push({ to: match.pathname, label: handle.breadcrumb });
      }
    }

    return crumbs.map((c, i) => ({
      ...c,
      isCurrent: i === crumbs.length - 1,
    }));
  }, [getLocale(), matches, orgName]);

  if (items.length === 0) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {items.map((item, i) => (
          <Fragment key={`${item.to}-${item.label}-${i}`}>
            {i > 0 ? <BreadcrumbSeparator /> : null}
            <BreadcrumbItem>
              {item.isCurrent ? (
                <BreadcrumbPage>{item.label}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={item.to}>{item.label}</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
